import { forwardRef } from "react";

import { alpha, styled, Theme } from "@mui/material";
import { Link, LinkProps, useLocation } from "react-router-dom";

type BaseProps = React.PropsWithChildren<{
  "data-testid"?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  variant: "full" | "outlined" | "text";
}>;

type ButtonProps = BaseProps & {
  onClick: (e: React.MouseEvent) => void;
  to?: never;
  keepSearchParams?: never;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

type AnchorProps = BaseProps & {
  to: LinkProps["to"];
  keepSearchParams?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  target?: "_blank";
  type?: never;
};

type Props = ButtonProps | AnchorProps;

const PrimaryButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (
    {
      children,
      "data-testid": dataTestId,
      disabled,
      fullWidth,
      icon = null,
      type,
      variant,
      ...rest
    },
    ref,
  ) => {
    const content = (
      <>
        {icon}
        {children}
      </>
    );

    if ("to" in rest && rest.to) {
      const LinkComponent = rest.keepSearchParams
        ? StyledSearchParamsLink
        : StyledLink;
      return (
        <LinkComponent
          data-testid={dataTestId}
          fullWidth={fullWidth}
          onClick={rest.onClick}
          ref={ref as React.Ref<HTMLAnchorElement>}
          rel={rest.target === "_blank" ? "noopener noreferrer" : undefined}
          target={rest.target}
          to={rest.to}
          variant={variant}
        >
          {content}
        </LinkComponent>
      );
    }

    return (
      <StyledButton
        data-testid={dataTestId}
        disabled={disabled}
        fullWidth={fullWidth}
        onClick={rest.onClick}
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        variant={variant}
        {...rest}
      >
        {content}
      </StyledButton>
    );
  },
);
PrimaryButton.displayName = "PrimaryButton";

type StyleProps = Pick<BaseProps, "disabled" | "fullWidth" | "variant">;

const baseStyles = ({
  disabled,
  fullWidth,
  theme,
  variant,
}: StyleProps & { theme: Theme }) => {
  const { cf } = theme.palette;
  const focusShadow = alpha(cf.cfPrimary, 0.65);
  const variants = {
    full: {
      backgroundColor: cf.cfPrimary,
      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.common.black, 0.1)}`,
      color: theme.palette.common.white,
      borderColor: "transparent",
      "& svg path": { fill: theme.palette.common.white },
      "&:not(:disabled):hover": {
        backgroundColor: cf.cfPrimaryDarker,
      },
      "&:not(:disabled):active": {
        backgroundColor: cf.cfPrimaryDarkest,
        outline: "none",
      },
      "&:not(:active):focus-visible": {
        outline: "none",
        boxShadow: `0 0 8px 0 ${focusShadow}`,
      },
      "&&:disabled": {
        backgroundColor: cf.cfDarkGrey,
      },
    },
    outlined: {
      backgroundColor: "transparent",
      color: cf.cfPrimary,
      borderColor: cf.cfPrimary,
      "& svg path": { fill: cf.cfPrimary },
      "&:not(:disabled):hover": {
        color: cf.cfPrimaryDarker,
        "& svg path": { fill: cf.cfPrimaryDarker },
      },
      "&:not(:disabled):active": {
        borderColor: cf.cfPrimaryDarkest,
        color: cf.cfPrimaryDarkest,
        "& svg path": { fill: cf.cfPrimaryDarkest },
      },
      "&:not(:active):focus-visible": {
        outline: "none",
        boxShadow: `0 0 8px 0 ${focusShadow}`,
      },
      "&&:disabled": {
        color: cf.cfDarkGrey,
        borderColor: cf.cfDarkGrey,
        "& svg path": { fill: cf.cfDarkGrey },
      },
    },
    text: {
      border: "1px solid transparent",
      backgroundColor: "transparent",
      color: cf.cfPrimary,
      "& svg path": { fill: cf.cfPrimary },
      "&:not(:disabled):hover": {
        color: cf.cfPrimaryDarker,
        "& svg path": { fill: cf.cfPrimaryDarker },
      },
      "&:not(:disabled):active": {
        color: cf.cfPrimaryDarkest,
        "& svg path": { fill: cf.cfPrimaryDarkest },
      },
      "&:not(:active):focus-visible": {
        outline: "none",
        boxShadow: `0 0 8px 0 ${focusShadow}`,
      },
      "&&:disabled": {
        color: cf.cfDarkGrey,
      },
    },
  } satisfies Record<typeof variant, unknown>;

  return {
    alignItems: "center",
    justifyContent: "center",
    display: "inline-flex",
    gap: theme.spacing(1),
    fontSize: "0.875rem",
    lineHeight: "20px",
    cursor: disabled ? "not-allowed" : "pointer",
    borderStyle: "solid",
    borderWidth: "1px",
    borderRadius: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    minHeight: 44,
    padding: `0 ${theme.spacing(1.5)}`,
    textDecoration: "none",
    width: fullWidth ? "100%" : "fit-content",
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      textDecoration: "none",
    },
    "&:active": {
      outline: "none",
    },
    "& svg": {
      fontSize: "20px",
    },
    ...variants[variant],
  };
};

const shouldForwardProp = (prop: string) =>
  !["fullWidth", "variant"].includes(prop);

const StyledButton = styled("button", { shouldForwardProp })<StyleProps>(
  baseStyles,
);

const StyledLink = styled(Link, { shouldForwardProp })<StyleProps>(
  ({ fullWidth, theme, variant }) => baseStyles({ fullWidth, theme, variant }),
);

const SearchParamsLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, ...props }, ref) => {
    const { search } = useLocation();
    const resolvedTo =
      typeof to === "string"
        ? `${to}${search}`
        : { ...to, search: `${to.search ?? ""}${search}` };
    return <Link ref={ref} to={resolvedTo} {...props} />;
  },
);
SearchParamsLink.displayName = "SearchParamsLink";

const StyledSearchParamsLink = styled(SearchParamsLink, {
  shouldForwardProp,
})<StyleProps>(({ fullWidth, theme, variant }) =>
  baseStyles({ fullWidth, theme, variant }),
);

export { PrimaryButton };
